/**
 * @file     GateSheet.tsx
 * @purpose  Presentational shell for every account gate (VOW access, auth,
 *           smart-match, alert signup): a bottom sheet on phones and a centred
 *           panel above that, with the scrim token, MUI's focus trap, optional
 *           Esc and backdrop close, a labelled heading, a step indicator and
 *           reduced-motion transitions. Holds no business logic; the gates own
 *           their flows and render their steps inside it.
 */
'use client';

import { forwardRef, useCallback, useId, useRef, type ReactElement, type ReactNode, type Ref } from 'react';
import { useOverlayBlocker } from '@/core/overlays/OverlayPriorityProvider';
import { useBackToClose } from '@/core/overlays/useBackToClose';
import Box from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import { GateStepTransition } from '@/components/auth/GateStepTransition';
import { firstField, firstFocusable } from '@/core/a11y/focusTarget';
import {
  SEMANTIC_CSS_VARS,
  layoutTokens,
  motionTokens,
  glassMaterial,
  semanticRoleColors,
} from '@/design-system/tokens';

const cssVar = (key: keyof typeof SEMANTIC_CSS_VARS) => `var(${SEMANTIC_CSS_VARS[key]})`;

export const gateColors = {
  surface: cssVar('surfaceCard'),
  border: cssVar('borderSubtle'),
  borderStrong: cssVar('borderStrong'),
  text: cssVar('textPrimary'),
  textSecondary: cssVar('textSecondary'),
  muted: cssVar('textMuted'),
  primary: cssVar('interactivePrimary'),
  onPrimary: cssVar('textOnPrimary'),
  focus: cssVar('interactiveFocusRing'),
  hover: cssVar('interactiveHover'),
  error: cssVar('statusError'),
  ...semanticRoleColors,
} as const;

const PHONE_QUERY = '(max-width: 599.95px)';
const SHEET_MAX_WIDTH = 460;
const CONTROL_HEIGHT = 48;

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return (
    <Slide
      direction="up"
      ref={ref}
      easing={{ enter: motionTokens.easing.spring, exit: motionTokens.easing.accelerate }}
      {...props}
    />
  );
});

export type GateSheetSteps = {
  current: number;
  total: number;
  /** Short name of the current step, shown after the count. */
  label?: string;
};

export type GateSheetProps = {
  open: boolean;
  /** Close handler. Omit it for a hard gate: no close button, Esc and the
   *  backdrop do nothing. */
  onClose?: () => void;
  /** Backdrop click closes the sheet. Defaults to true whenever onClose is set. */
  closeOnBackdrop?: boolean;
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Keep the heading for screen readers (aria-labelledby) but hide it,
   *  for hosts whose content already shows its own heading. */
  titleHidden?: boolean;
  /** Progress through a numbered flow. Renders "Step n of m" plus bars. */
  steps?: GateSheetSteps;
  /** Header label for un-numbered screens (sign in, returning member). */
  statusLabel?: string;
  /** Header back control. Omit when the step has no previous step. */
  onBack?: () => void;
  /** Identity of the visible step. A change slides the body (or crossfades
   *  under reduced motion). */
  stepKey?: string;
  stepDirection?: 'forward' | 'back';
  /** Phones: grow the sheet to the full dynamic viewport and leave room under
   *  the last button, so the on-screen keyboard can never hide it. */
  fillOnPhone?: boolean;
  /** Only for hosts that must stack above a non-MUI overlay. Use a zLayers value. */
  zIndex?: number;
  /** Replaces the header label with the host's own row (the listing a sheet
   *  acts on). A step count then sits above the bars instead. */
  header?: ReactNode;
  /** Panel width from 600px up. Defaults to 460. */
  maxWidth?: number;
  /** The scrollable body, for hosts that manage focus on step change. */
  bodyRef?: Ref<HTMLDivElement>;
  /** Stable hook for tests and screenshots. */
  testId?: string;
  children: ReactNode;
};

export function GateSheet({
  open,
  onClose,
  closeOnBackdrop,
  eyebrow,
  title,
  titleHidden = false,
  steps,
  statusLabel,
  onBack,
  stepKey,
  stepDirection = 'forward',
  fillOnPhone = false,
  zIndex,
  header,
  maxWidth = SHEET_MAX_WIDTH,
  bodyRef,
  testId,
  children,
}: GateSheetProps) {
  const { t } = useTranslation();
  const titleId = useId();
  useOverlayBlocker(open);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const dismissible = Boolean(onClose);
  const backdropCloses = closeOnBackdrop ?? dismissible;
  useBackToClose(open && dismissible, () => onClose?.());
  const radius = layoutTokens.radius.xl;
  const innerBodyRef = useRef<HTMLDivElement | null>(null);
  const setBodyRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerBodyRef.current = node;
      if (typeof bodyRef === 'function') bodyRef(node);
      else if (bodyRef) (bodyRef as { current: HTMLDivElement | null }).current = node;
    },
    [bodyRef],
  );

  // MUI focuses the dialog container on open; move focus on to the first field
  // so typing can start at once. Focus returns to the trigger on close (MUI's
  // restore focus). A step that autofocused its own field keeps it. A step
  // with no field (the showing sheet's date and time pills) focuses its first
  // content control, so focus never sits on the container outside role="dialog".
  const contentRef = useRef<HTMLDivElement | null>(null);
  const focusFirstField = useCallback(() => {
    const body = innerBodyRef.current;
    if (!body) return;
    const active = document.activeElement;
    const activeInBody = active instanceof HTMLElement && body.contains(active);
    if (activeInBody && active.matches('input, select, textarea')) return;
    const field = firstField(body);
    if (field) {
      field.focus({ preventScroll: true });
      return;
    }
    if (!activeInBody) (firstFocusable(contentRef.current ?? body) ?? firstFocusable(body))?.focus({ preventScroll: true });
  }, []);

  const headerLabel = steps
    ? [
        t('gate.sheet.stepCount', {
          defaultValue: 'Step {{current}} of {{total}}',
          current: steps.current,
          total: steps.total,
        }),
        steps.label,
      ]
        .filter(Boolean)
        .join(' · ')
    : statusLabel;

  // Phone sheets travel the full screen height, so they get the slow beat on
  // the spring curve; the centred panel fades and settles on the base beat.
  const enterMs = reduceMotion ? 0 : isPhone ? motionTokens.durations.slow : motionTokens.durations.base;
  const exitMs = reduceMotion ? 0 : motionTokens.durations.fast;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (!onClose) return;
        if (reason === 'backdropClick' && !backdropCloses) return;
        onClose();
      }}
      disableEscapeKeyDown={!dismissible}
      aria-labelledby={titleId}
      fullWidth
      maxWidth={false}
      slots={{ transition: isPhone ? SlideUp : Fade }}
      transitionDuration={{ enter: enterMs, exit: exitMs }}
      data-testid={testId}
      data-gate-sheet-layout={isPhone ? (fillOnPhone ? 'fill' : 'sheet') : 'panel'}
      sx={{
        ...(zIndex !== undefined ? { zIndex } : null),
        '& .MuiDialog-container': { alignItems: { xs: 'flex-end', sm: 'center' } },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: gateColors.scrim } },
        transition: { onEntered: focusFirstField },
        paper: {
          'data-gate-sheet': '',
          sx: {
            m: { xs: 0, sm: 3 },
            width: '100%',
            maxWidth: { xs: '100%', sm: maxWidth },
            maxHeight: {
              xs: 'calc(100dvh - 12px - env(safe-area-inset-top, 0px))',
              sm: 'calc(100% - 48px)',
            },
            ...(fillOnPhone && isPhone
              ? { height: 'calc(100dvh - 12px - env(safe-area-inset-top, 0px))' }
              : null),
            borderRadius: { xs: `${radius}px ${radius}px 0 0`, sm: `${radius}px` },
            ...glassMaterial('thick'),
            borderBottomWidth: { xs: 0, sm: 1 },
            ...(isPhone || reduceMotion
              ? null
              : { animation: `relta-overlay-in ${motionTokens.durations.base}ms ${motionTokens.easing.decelerate}` }),
            color: gateColors.text,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        } as object,
      }}
    >
      <Box
        ref={setBodyRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          px: { xs: 5, sm: 6 },
          pt: { xs: 3, sm: 4 },
          pb: {
            xs: fillOnPhone
              ? 'calc(96px + env(safe-area-inset-bottom, 0px))'
              : 'calc(24px + env(safe-area-inset-bottom, 0px))',
            sm: 6,
          },
        }}
      >
        {isPhone && (
          <Box
            aria-hidden
            sx={{ width: 36, height: 4, mx: 'auto', mb: 2, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: gateColors.borderStrong }}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 44 }}>
          {onBack && (
            <IconButton
              aria-label={t('gate.sheet.back', { defaultValue: 'Back' })}
              onClick={onBack}
              sx={{ width: 44, height: 44, ml: -2.5, color: gateColors.muted }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </IconButton>
          )}
          {header ? (
            <Box sx={{ flex: 1, minWidth: 0 }}>{header}</Box>
          ) : (
          <Typography
            component="p"
            sx={{
              flex: 1,
              minWidth: 0,
              m: 0,
              fontSize: 13,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              color: gateColors.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {headerLabel}
          </Typography>
          )}
          {dismissible && (
            <IconButton
              aria-label={t('gate.sheet.close', { defaultValue: 'Close' })}
              onClick={onClose}
              sx={{ width: 44, height: 44, mr: -2.5, color: gateColors.muted }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </IconButton>
          )}
        </Box>

        {header && headerLabel ? (
          <Typography
            component="p"
            sx={{ m: 0, mt: 3, fontSize: 13, fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: gateColors.muted }}
          >
            {headerLabel}
          </Typography>
        ) : null}
        {steps && steps.total > 1 && (
          <Box aria-hidden sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {Array.from({ length: steps.total }, (_, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: 3,
                  borderRadius: `${layoutTokens.radius.pill}px`,
                  backgroundColor: i < steps.current ? gateColors.primary : gateColors.border,
                  transition: reduceMotion ? 'none' : `background-color ${motionTokens.durations.normal}ms ${motionTokens.easing.standard}`,
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={titleHidden ? visuallyHiddenSx : { mt: 5, mb: stepKey ? 3 : 5 }}>
          {eyebrow && !titleHidden && (
            <Typography
              component="p"
              sx={{ m: 0, mb: 2, fontSize: 13, fontWeight: 500, color: gateColors.link }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            id={titleId}
            component="h2"
            sx={{ m: 0, fontSize: 24, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.015em', textWrap: 'pretty', color: gateColors.text }}
          >
            {title}
          </Typography>
        </Box>

        {stepKey ? (
          <GateStepTransition stepKey={stepKey} direction={stepDirection}>
            {/* The transition clips overflow; this keeps a focused field's
                floating label inside the clip. */}
            <Box ref={contentRef} sx={{ pt: 2 }}>{children}</Box>
          </GateStepTransition>
        ) : (
          children
        )}
      </Box>
    </Dialog>
  );
}

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
} as const;

const controlBase = {
  minHeight: CONTROL_HEIGHT,
  borderRadius: `${layoutTokens.radius.md}px`,
  textTransform: 'none' as const,
  fontSize: 16,
  boxShadow: 'none',
  transition: `background-color ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}, border-color ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`,
  '&:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

/** The one filled action on a step. Shows a spinner while `pending`. */
export function GatePrimaryButton({ pending = false, children, sx, disabled, ...rest }: ButtonProps & { pending?: boolean }) {
  return (
    <Button
      {...rest}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      fullWidth
      disableElevation
      sx={{
        ...controlBase,
        fontWeight: 500,
        backgroundColor: gateColors.primary,
        color: gateColors.onPrimary,
        '&:hover': { backgroundColor: gateColors.primaryHover, boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: gateColors.primary, color: gateColors.onPrimary, opacity: 0.55 },
        ...(sx as object),
      }}
    >
      {pending ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : children}
    </Button>
  );
}

/** Outlined companion action (Back, Already have an account). */
export function GateSecondaryButton({ children, sx, ...rest }: ButtonProps) {
  return (
    <Button
      {...rest}
      variant="outlined"
      sx={{
        ...controlBase,
        px: 5,
        flexShrink: 0,
        fontWeight: 500,
        fontSize: 15,
        color: gateColors.text,
        borderColor: gateColors.borderStrong,
        '&:hover': { borderColor: gateColors.borderStrong, backgroundColor: gateColors.hover },
        ...(sx as object),
      }}
    >
      {children}
    </Button>
  );
}

/** Inline text action. A real button, so it is reachable and operable by
 *  keyboard, replacing the clickable spans the gates used to carry. */
export function GateTextButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'inline',
        p: 0,
        m: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontWeight: 500,
        color: gateColors.link,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        textDecoration: 'underline',
        textDecorationColor: 'transparent',
        textUnderlineOffset: '3px',
        transition: `text-decoration-color ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`,
        '&:hover:not(:disabled)': { color: gateColors.linkHover, textDecorationColor: 'currentColor' },
        '&:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2, borderRadius: `${layoutTokens.radius.sm}px` },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {children}
    </Box>
  );
}

/** Back plus primary on one row, as the design's step footer. */
export function GateActions({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>{children}</Box>;
}

/** Small centred helper line under the actions. */
export function GateNote({ children }: { children: ReactNode }) {
  return (
    <Typography component="p" sx={{ m: 0, textAlign: 'center', fontSize: 14, lineHeight: 1.5, color: gateColors.textSecondary }}>
      {children}
    </Typography>
  );
}
